import * as React from 'react'
import { Building2, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { TenantPicker } from '@/components/visitone/tenant-picker'
import { useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import { useT } from '@/components/visitone/visitone-provider'
import { useTenantContext } from '@/components/tenant/tenant-context'

const triggerBase =
  'inline-flex min-h-10 min-w-0 max-w-full items-center gap-2 rounded-sm px-2.5 py-1 text-left text-sm font-medium text-fg-strong md:max-w-xs'

/*
 * The current tenant, shown in the shell at every breakpoint (VIS-326 AC 1).
 * With more than one tenant it is a button that opens the Switch tenant
 * dialog. Choosing a tenant switches at once unless there are unsaved
 * changes; then the shared unsaved-changes dialog asks first (VIS-321 AC 10),
 * and Keep editing stays in this tenant with the changes. With one tenant it
 * is text.
 */
export function TenantSwitcher() {
  const t = useT()
  const { currentTenant, tenants, phase, requestSwitch } = useTenantContext()
  const { confirmThen } = useUnsavedChanges()
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  const close = () => setOpen(false)

  if (phase === 'switching') {
    return (
      <span className={`${triggerBase} text-fg-muted`}>
        <Loader2 aria-hidden="true" className="size-4 shrink-0 motion-safe:animate-spin" />
        <span className="min-w-0 wrap-anywhere">{t('core.tenant.switchingShort')}</span>
      </span>
    )
  }

  if (phase === 'failed' || !currentTenant) {
    return (
      <p className={`${triggerBase} text-fg-muted`}>
        <Building2 aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 wrap-anywhere">{t('core.tenant.noTenant')}</span>
      </p>
    )
  }

  const label = (
    <>
      <Building2 aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
      <span className="min-w-0 wrap-anywhere">
        <span className="sr-only">{t('core.tenant.currentPrefix')}</span> {currentTenant.name}
      </span>
    </>
  )

  if (tenants.length <= 1) {
    return <p className={triggerBase}>{label}</p>
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className={`${triggerBase} border border-line bg-surface transition-colors hover:border-line-strong hover:bg-surface-hover`}
      >
        {label}
        <ChevronsUpDown aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
      </button>

      <Modal open={open} onClose={close} labelledBy="tenant-switch-title">
        <div className="flex flex-col gap-4">
          <h2 id="tenant-switch-title" className="text-base font-semibold text-fg-strong">
            {t('core.tenant.dialogTitle')}
          </h2>
          <TenantPicker
            tenants={tenants}
            currentId={currentTenant.id}
            onChoose={(option) => {
              close()
              const tenant = tenants.find((entry) => entry.id === option.id)
              if (!tenant || tenant.id === currentTenant.id) return
              confirmThen({ kind: 'switch', tenant: tenant.name }, () => requestSwitch(tenant), {
                returnFocus: triggerRef.current,
              })
            }}
          />
          <div className="flex justify-end">
            <Button variant="secondary" onClick={close}>
              {t('core.tenant.close')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
